import { useCallback, useEffect, useState } from 'react';
import AdmGate from '@/pages/adm/components/AdmGate';
import AdmShell from '@/pages/adm/components/AdmShell';
import {
  grantAdm,
  listAdmAssignments,
  lookupUserByEmail,
  revokeAdm,
  suspendAdm,
} from '@/pages/adm/lib/admApi';
import type { AdmAssignmentRow } from '@/pages/adm/lib/admApi';

const SCOPE_LABEL: Record<string, string> = {
  root: 'ROOT ADM',
  rg: 'ADM for RG',
  og: 'ADM for OG',
};

const STATUS_LABEL: Record<string, string> = {
  active: '啟用',
  suspended: '停權',
  revoked: '已撤銷',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  suspended: 'bg-foreground-200 text-foreground-600',
  revoked: 'bg-foreground-100 text-foreground-400',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const maskedLocal = local.length <= 2 ? `${local[0]}***` : `${local.slice(0, 2)}***`;
  return `${maskedLocal}${domain}`;
}

function shortId(id: string): string {
  if (!id) return '—';
  return id.length > 8 ? `…${id.slice(-8)}` : id;
}

export default function AdmRootPage() {
  return (
    <AdmGate requiredScope="root">
      {(auth) => (
        <AdmShell auth={auth} active="root">
          <RootDashboard />
        </AdmShell>
      )}
    </AdmGate>
  );
}

function RootDashboard() {
  const [assignments, setAssignments] = useState<AdmAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // 授權表單
  const [grantEmail, setGrantEmail] = useState('');
  const [grantScope, setGrantScope] = useState<'rg' | 'og'>('rg');
  const [grantReason, setGrantReason] = useState('');
  const [lookupResult, setLookupResult] = useState<{ user_id: string; email: string } | null>(null);
  const [lookupMsg, setLookupMsg] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const a = await listAdmAssignments();
      setAssignments(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLookup = async () => {
    const email = grantEmail.trim();
    if (!email) {
      setLookupMsg('請輸入完整 Email。');
      return;
    }
    setLookupLoading(true);
    setLookupMsg(null);
    setLookupResult(null);
    setActionMsg(null);
    try {
      const found = await lookupUserByEmail(email);
      if (!found) {
        setLookupMsg('此 Email 尚未建立 AUM1001 帳號，請先至 /account 註冊並完成 Email 驗證。');
      } else {
        setLookupResult(found);
      }
    } catch (err) {
      setLookupMsg(err instanceof Error ? err.message : '查找失敗');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!lookupResult) {
      setActionMsg('請先輸入 Email 查找使用者。');
      return;
    }
    setBusy('grant');
    setActionMsg(null);
    try {
      await grantAdm(lookupResult.user_id, grantScope, grantReason);
      setActionMsg(`已授予 ${lookupResult.email} 的 ${SCOPE_LABEL[grantScope]}。`);
      setGrantEmail('');
      setGrantReason('');
      setLookupResult(null);
      await load();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '授權失敗');
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async (a: AdmAssignmentRow) => {
    setBusy(String(a.id));
    setActionMsg(null);
    try {
      await revokeAdm(a.user_id, a.scope, '由 ROOT ADM 撤銷');
      setActionMsg(`已撤銷 ${a.email || '該使用者'} 的 ${SCOPE_LABEL[a.scope] || a.scope}。`);
      await load();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '撤銷失敗');
    } finally {
      setBusy(null);
    }
  };

  const handleSuspend = async (a: AdmAssignmentRow) => {
    setBusy(String(a.id));
    setActionMsg(null);
    try {
      await suspendAdm(a.user_id, a.scope, '由 ROOT ADM 停權');
      setActionMsg(`已停權 ${a.email || '該使用者'} 的 ${SCOPE_LABEL[a.scope] || a.scope}。`);
      await load();
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '停權失敗');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-sm text-foreground-500 mt-3">載入授權資料……</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center space-y-3">
        <p className="text-sm text-rose-600">{error}</p>
        <button
          onClick={load}
          className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm cursor-pointer"
        >
          重試
        </button>
      </div>
    );
  }

  const activeAssignments = assignments.filter((a) => a.status === 'active');

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          授權治理中心
        </h1>
        <p className="text-sm text-foreground-500">
          ROOT ADM 總覽。管理領域 ADM 授權（RG／OG），並查看授權歷史。管理權與個人的專業資格（施炁者培育、Mentor 認證）完全分離。
        </p>
      </header>

      {actionMsg && <p className="text-xs text-foreground-500 bg-background-100 border border-foreground-200/50 rounded-lg px-3 py-2">{actionMsg}</p>}

      {/* 授權表單 */}
      <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 md:p-5 space-y-3">
        <h2 className="text-sm font-semibold text-foreground-800">授予領域 ADM</h2>
        <p className="text-xs text-foreground-500 leading-relaxed">
          ADM 授權與個人專業資格（施炁者、Mentor）完全分離。輸入對方完整 Email，精確查找已完成 AUM1001 全站註冊的使用者，再授予 RG／OG 管理權。
        </p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1 flex-1 min-w-[260px]">
            <label className="block text-xs text-foreground-500">完整 Email</label>
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => {
                setGrantEmail(e.target.value);
                setLookupResult(null);
                setLookupMsg(null);
              }}
              placeholder="輸入對方已註冊的完整 Email"
              className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
            />
          </div>
          <button
            onClick={handleLookup}
            disabled={lookupLoading || !grantEmail.trim()}
            className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-50"
          >
            {lookupLoading ? '查找中……' : '查找'}
          </button>
        </div>

        {lookupMsg && (
          <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-3 py-2">{lookupMsg}</p>
        )}

        {lookupResult && (
          <div className="space-y-3 border-t border-foreground-200/50 pt-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground-800">{maskEmail(lookupResult.email)}</span>
                <span className="text-xs text-foreground-400">ID {shortId(lookupResult.user_id)}</span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                已找到 AUM1001 帳號
              </span>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1">
                <label className="block text-xs text-foreground-500">領域</label>
                <select
                  value={grantScope}
                  onChange={(e) => setGrantScope(e.target.value as 'rg' | 'og')}
                  className="px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-accent-300 cursor-pointer"
                >
                  <option value="rg">ADM for RG</option>
                  <option value="og">ADM for OG</option>
                </select>
              </div>
              <div className="space-y-1 flex-1 min-w-[200px]">
                <label className="block text-xs text-foreground-500">備註（理由）</label>
                <input
                  type="text"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  placeholder="選填"
                  className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
                />
              </div>
              <button
                onClick={handleGrant}
                disabled={busy === 'grant'}
                className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
              >
                {busy === 'grant' ? '授予中……' : '確認授予'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* 授權現況 */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground-800">授權現況</h2>
        {activeAssignments.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-6 text-center">
            目前沒有啟用中的領域 ADM 授權。
          </p>
        ) : (
          <div className="space-y-2">
            {activeAssignments.map((a) => (
              <div
                key={a.id}
                className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
              >
                <div className="space-y-1 min-w-[180px]">
                  <p className="text-sm font-medium text-foreground-800">{a.email || '（無信箱）'}</p>
                  <p className="text-xs text-foreground-400">
                    {SCOPE_LABEL[a.scope] || a.scope} &middot; 授予於 {formatDate(a.granted_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">啟用</span>
                  <button
                    onClick={() => handleSuspend(a)}
                    disabled={busy === String(a.id)}
                    className="whitespace-nowrap px-3 py-1.5 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-100 cursor-pointer disabled:opacity-50"
                  >
                    停權
                  </button>
                  <button
                    onClick={() => handleRevoke(a)}
                    disabled={busy === String(a.id)}
                    className="whitespace-nowrap px-3 py-1.5 rounded-md border border-rose-200 text-xs text-rose-600 hover:bg-rose-50 cursor-pointer disabled:opacity-50"
                  >
                    撤銷
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 授權歷史 */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground-800">授權歷史</h2>
        {assignments.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-6 text-center">
            尚無授權紀錄。
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-foreground-400 border-b border-foreground-200/50">
                  <th className="py-2 pr-4 font-medium">使用者</th>
                  <th className="py-2 pr-4 font-medium">領域</th>
                  <th className="py-2 pr-4 font-medium">狀態</th>
                  <th className="py-2 pr-4 font-medium">授予時間</th>
                  <th className="py-2 font-medium">備註</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className="border-b border-foreground-100">
                    <td className="py-2.5 pr-4 text-foreground-700 whitespace-nowrap">{a.email || '—'}</td>
                    <td className="py-2.5 pr-4 text-foreground-600 whitespace-nowrap">{SCOPE_LABEL[a.scope] || a.scope}</td>
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status] || 'bg-foreground-100 text-foreground-500'}`}>
                        {STATUS_LABEL[a.status] || a.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-foreground-500 whitespace-nowrap">{formatDate(a.granted_at)}</td>
                    <td className="py-2.5 text-foreground-400">{a.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}